import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { fmtMoeda } from '@/lib/utils/formatters';
import { useSegregacaoDetalhar } from '@/services/SimplesNacional/Segregacao/hooks/useSegregacaoDetalhar';
import type { SegregacaoDetalharPayload } from '@/services/SimplesNacional/Segregacao/api/types';

interface DetalhesLinhaModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payload: Omit<SegregacaoDetalharPayload, 'LinhaSegregada'> | null;
  linha: string | null;
}

export function DetalhesLinhaModal({
  open,
  onOpenChange,
  payload,
  linha,
}: DetalhesLinhaModalProps) {
  const detalharPayload = open && payload && linha ? { ...payload, LinhaSegregada: linha } : null;

  const { data, isLoading } = useSegregacaoDetalhar(detalharPayload);

  const itens = data?.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Detalhamento da Linha</DialogTitle>
          <DialogDescription>
            Documentos que compõem a linha <strong>{linha ?? '—'}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mr-2" />
              <span className="text-sm text-muted-foreground">Carregando documentos...</span>
            </div>
          ) : itens.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Nenhum documento encontrado para esta linha.
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b bg-muted/40">
                  <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">
                    Chave de Acesso
                  </th>
                  <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">
                    Produto
                  </th>
                  <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">NCM</th>
                  <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">CFOP</th>
                  <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">
                    Valor Prod.
                  </th>
                  <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">
                    Valor Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {itens.map((item, i) => (
                  <tr
                    key={i}
                    className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                  >
                    <td
                      className="px-3 py-2 font-mono text-[10px] text-muted-foreground max-w-[180px] truncate"
                      title={item.ChaveAcessoDOC}
                    >
                      {item.ChaveAcessoDOC || '—'}
                    </td>
                    <td className="px-3 py-2 max-w-[140px] truncate" title={item.Produto}>
                      {item.Produto || '—'}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{item.NCM || '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground">{item.CFOP || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fmtMoeda(parseFloat(item.ValorProd) || 0)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">
                      {fmtMoeda(parseFloat(item.ValTot) || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border-t pt-3 text-xs text-muted-foreground">
          {itens.length > 0 && <span>{itens.length} documento(s) encontrado(s)</span>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
